import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import { useLoadingBar } from "react-top-loading-bar";
import { CSVLink } from "react-csv";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import ContentLoader from "../loader/ContentLoader";
import DetailView from "../location/DetailView";
import { suppliersService } from "../../services/suppliers.service";
import { countriesService } from "../../services/countries.service";
import { statesService } from "../../services/states.service";
import { citiesService } from "../../services/cities.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    minHeight: 34,
    height: 34,
    fontSize: 13,
    boxShadow: "none",
    "&:hover": { borderColor: state.isFocused ? "#667eea" : "#e8edf5" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const SupplierList = () => {
  const { start, complete } = useLoadingBar({ color: "blue", height: 2 });
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [supplierRes, setSupplierRes] = useState({
    pagination: {},
    suppliers: [],
  });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  const initialFilters = {
    search: "",
    country_id: "",
    state_id: "",
    city_id: "",
    status: "",
  };
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const [show, setShow] = useState(false);
  const [viewSupplier, setViewSupplier] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // option values are ids (the suppliers API filters by country_id/state_id/city_id)
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  useEffect(() => {
    loadSuppliers();
  }, [pagingLimit, pageValue, appliedFilters]);

  useEffect(() => {
    loadLocationOptions(countriesService, {}, setCountryOptions);
  }, []);

  const loadLocationOptions = async (service, extra, setter) => {
    try {
      const res = await service.list({ page: 1, status: "active", ...extra });
      setter(
        (res?.resData?.data || []).map((i) => ({
          value: i.id,
          label: i.name,
          id: i.id,
        })),
      );
    } catch (err) {
      setter([]);
    }
  };

  const handleCountryFilter = (o) => {
    setFilters({ ...filters, country_id: o ? o.value : "", state_id: "", city_id: "" });
    setStateOptions([]);
    setCityOptions([]);
    if (o)
      loadLocationOptions(statesService, { country_id: o.id }, setStateOptions);
  };

  const handleStateFilter = (o) => {
    setFilters({ ...filters, state_id: o ? o.value : "", city_id: "" });
    setCityOptions([]);
    if (o)
      loadLocationOptions(citiesService, { state_id: o.id }, setCityOptions);
  };

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon });
  };

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      start();
      const limit = Number(pagingLimit);
      const res = await suppliersService.list({
        offset: (Number(pageValue) - 1) * limit,
        limit,
        ...appliedFilters,
      });
      if (res?.success) {
        setSupplierRes({
          pagination: res.resData?.pagination || {},
          suppliers: res.resData?.data || [],
        });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      complete();
    }
  };

  const handleStatusChange = async (supplier, newStatus) => {
    if (newStatus === supplier?.status) return;
    const result = await withReactContent(Swal).fire({
      title: "Change status?",
      text: `Are you sure you want to mark ${supplier?.agency_name} as ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await suppliersService.updateStatus(supplier?.id, {
        status: newStatus,
      });
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      loadSuppliers();
    }
  };

  const handleDeleteSupplier = async (supplier) => {
    const result = await withReactContent(Swal).fire({
      title: "Delete supplier?",
      text: `Are you sure you want to delete ${supplier?.agency_name}? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d93025",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await suppliersService.delete(supplier?.id);
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      loadSuppliers();
    }
  };

  const handleEditSupplier = (supplier) => {
    navigate("/suppliers/add", { state: { supplierId: supplier?.id } });
  };

  const handleViewSupplier = async (supplierId) => {
    setShow(true);
    setViewSupplier(null);
    try {
      setViewLoading(true);
      const res = await suppliersService.details(supplierId);
      if (res?.success) {
        setViewSupplier(res.resData?.supplier ?? res.resData);
      } else {
        toast.error(res?.message);
        setShow(false);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
      setShow(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setStateOptions([]);
    setCityOptions([]);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  };

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  };

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  };

  const handlePagination = (pageValueInput) => {
    setPageValue(pageValueInput);
  };

  const suppliers = supplierRes?.suppliers || [];
  const totalRecords = supplierRes?.pagination?.total || 0;
  const totalPage = Math.max(1, Math.ceil(totalRecords / Number(pagingLimit)));
  const activeCount = suppliers.filter((v) => v?.status === "active").length;
  const fromRecord =
    totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5)
      return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue])]
      .filter((n) => n >= 1 && n <= totalPage)
      .sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  };

  const csvHeaders = [
    { label: "Supplier Code", key: "supplier_code" },
    { label: "Agency Name", key: "agency_name" },
    { label: "Head Person", key: "head_person" },
    { label: "Mobile", key: "mobile" },
    { label: "Email", key: "email" },
    { label: "City", key: "city" },
    { label: "State", key: "state" },
    { label: "Country", key: "country" },
    { label: "Wallet Balance", key: "wallet.current_balance" },
    { label: "Status", key: "status" },
  ];

  const pageBtn = (active, disabled) => ({
    minWidth: 32,
    height: 32,
    padding: "0 8px",
    borderRadius: 8,
    border: active ? "1px solid #0d6efd" : "1px solid #e8edf5",
    background: active ? "#0d6efd" : "#fff",
    color: active ? "#fff" : disabled ? "#c3cad8" : "#444",
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
  });

  const chip = (color, bg, border) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,
    background: bg,
    border: `1px solid ${border}`,
    color,
    fontSize: 13,
  });

  const filterInput = {
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    fontSize: 13,
    height: 34,
  };

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4
              className="m-0 fw-bold"
              style={{ color: "#1a202c", fontSize: 20 }}
            >
              Suppliers
            </h4>
            <nav
              className="d-flex align-items-center gap-1 mt-1"
              style={{ fontSize: 12, color: "#8a9ab5" }}
            >
              <Link to="/" style={{ color: "#8a9ab5" }}>
                <i className="bi bi-house-door"></i>
              </Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span>Suppliers</span>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>
                Supplier List
              </span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Link
              to="/suppliers/add"
              className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
              style={{
                background: "#4f3df5",
                borderRadius: 8,
                height: 34,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <i className="bi bi-plus-lg"></i> Add Supplier
            </Link>
            <CSVLink
              data={suppliers}
              headers={csvHeaders}
              filename="suppliers.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{
                background: "#fff",
                border: "1px solid #e8edf5",
                borderRadius: 8,
                height: 34,
                fontSize: 13,
                color: "#444",
              }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        <form
          onSubmit={handleApply}
          className="card border-0 shadow-sm mb-3"
          style={{ borderRadius: 14 }}
        >
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md">
                <input
                  type="text"
                  className="form-control"
                  name="search"
                  placeholder="Search code, agency, head person, mobile, email"
                  value={filters.search}
                  onChange={handleFilterChange}
                  style={filterInput}
                />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={countryOptions}
                  value={
                    countryOptions.find((o) => o.value === filters.country_id) || null
                  }
                  onChange={handleCountryFilter}
                  placeholder="Select Country"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={stateOptions}
                  value={
                    stateOptions.find((o) => o.value === filters.state_id) || null
                  }
                  onChange={handleStateFilter}
                  placeholder="Select State"
                  isClearable
                  isDisabled={!filters.country_id}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={cityOptions}
                  value={
                    cityOptions.find((o) => o.value === filters.city_id) || null
                  }
                  onChange={(o) => setFilters({ ...filters, city_id: o ? o.value : "" })}
                  placeholder="Select City"
                  isClearable
                  isDisabled={!filters.state_id}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={STATUS_FILTER_OPTIONS}
                  value={
                    STATUS_FILTER_OPTIONS.find(
                      (o) => o.value === filters.status,
                    ) || null
                  }
                  onChange={(o) =>
                    setFilters({ ...filters, status: o ? o.value : "" })
                  }
                  placeholder="Select Status"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-auto d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
                  style={{
                    background: "#4f3df5",
                    borderRadius: 8,
                    height: 34,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <i className="bi bi-search"></i> Apply
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn btn-sm d-flex align-items-center gap-1 px-3"
                  style={{
                    background: "#fff",
                    border: "1px solid #e8edf5",
                    borderRadius: 8,
                    height: 34,
                    fontSize: 13,
                    color: "#444",
                  }}
                >
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{totalRecords}</b> Total Suppliers
          </span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{activeCount}</b> Active on this page
          </span>
          <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{suppliers.length - activeCount}</b> Inactive on this page
          </span>
        </div>

        <div
          className="card border-0 shadow-sm"
          style={{ borderRadius: 14, overflow: "hidden" }}
        >
          <div style={{ position: "relative" }}>
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                }}
              >
                <ContentLoader loaderType="circleSpin" />
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: "#f8f9fe" }}>
                    {[
                      "Supplier",
                      "Head Person",
                      "Contact",
                      "Location",
                      "Wallet",
                      "Status",
                      "Actions",
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#8a9ab5",
                          textTransform: "uppercase",
                          letterSpacing: "0.6px",
                          borderBottom: "2px solid #e8edf5",
                          padding: "10px 12px",
                          whiteSpace: "nowrap",
                          background: "#f8f9fe",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {suppliers.map((supplier) => (
                    <tr
                      key={supplier?.id}
                      style={{ borderBottom: "1px solid #f0f2f5" }}
                    >
                      <td className="ps-3">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: 36,
                              height: 36,
                              background: "#e8f0fe",
                              flexShrink: 0,
                            }}
                          >
                            <i
                              className="bi bi-person-badge text-primary"
                              style={{ fontSize: 14 }}
                            ></i>
                          </div>
                          <div>
                            <div
                              className="fw-semibold text-dark"
                              style={{ fontSize: 13 }}
                            >
                              {supplier?.agency_name}
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: 11 }}
                            >
                              {supplier?.supplier_code}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="fw-medium" style={{ fontSize: 13 }}>
                          {supplier?.head_person}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: 13 }}>
                          <i
                            className="bi bi-telephone-fill me-1 text-primary"
                            style={{ fontSize: 10 }}
                          ></i>
                          {supplier?.mobile}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {supplier?.email}
                        </div>
                      </td>
                      <td>
                        <div className="fw-medium" style={{ fontSize: 13 }}>
                          {supplier?.city?.name}
                          {supplier?.state?.name ? `, ${supplier.state.name}` : ""}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          <i
                            className="bi bi-geo-alt-fill me-1 text-danger"
                            style={{ fontSize: 10 }}
                          ></i>
                          {supplier?.country?.name}
                          {supplier?.pincode ? ` - ${supplier.pincode}` : ""}
                        </div>
                      </td>
                      <td>
                        <div className="fw-semibold" style={{ fontSize: 13 }}>
                          {"₹ " + (supplier?.wallet?.current_balance ?? "0.00")}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          Hold: {supplier?.wallet?.hold_balance ?? "0.00"}
                        </div>
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={supplier?.status}
                          onChange={(e) =>
                            handleStatusChange(supplier, e.target.value)
                          }
                          style={{
                            width: "auto",
                            minWidth: 0,
                            height: 28,
                            padding: "0 26px 0 10px",
                            backgroundPosition: "right 8px center",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 600,
                            backgroundColor:
                              supplier?.status === "active"
                                ? "#d4edda"
                                : "#f8d7da",
                            color:
                              supplier?.status === "active"
                                ? "#155724"
                                : "#721c24",
                            border: "none",
                          }}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleViewSupplier(supplier?.id)}
                            title="View"
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              background: "#e6f7f1",
                              color: "#0f8a5f",
                              border: "none",
                              padding: 0,
                            }}
                          >
                            <i
                              className="bi bi-eye-fill"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleEditSupplier(supplier)}
                            title="Edit"
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              background: "#e8f0fe",
                              color: "#0d6efd",
                              border: "none",
                              padding: 0,
                            }}
                          >
                            <i
                              className="bi bi-pencil-fill"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleDeleteSupplier(supplier)}
                            title="Delete"
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              background: "#fce8e6",
                              color: "#d93025",
                              border: "none",
                              padding: 0,
                            }}
                          >
                            <i
                              className="bi bi-trash-fill"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && suppliers.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center text-muted py-4"
                        style={{ fontSize: 13 }}
                      >
                        No suppliers found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div
            className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3"
            style={{
              borderTop: "1px solid #eef1f6",
              fontSize: 13,
              color: "#6b7a90",
            }}
          >
            <div>
              Showing{" "}
              <b>
                {fromRecord}–{toRecord}
              </b>{" "}
              of <b>{totalRecords}</b> suppliers
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>Rows</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 95, borderRadius: 8 }}
                value={pagingLimit}
                onChange={handlePagingLimit}
              >
                {["10", "20", "50", "100", "500"].map((v) => (
                  <option key={v} value={v}>
                    {v}/page
                  </option>
                ))}
              </select>
              <span>Go</span>
              <input
                type="number"
                min="1"
                max={totalPage}
                className="form-control form-control-sm text-center"
                style={{ width: 60, borderRadius: 8 }}
                value={goPage}
                onChange={(e) => setGoPage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGoPage()}
                placeholder={String(pageValue)}
              />
              <span>/ {totalPage}</span>
              <button
                type="button"
                style={pageBtn(false, false)}
                onClick={handleGoPage}
              >
                Go
              </button>
              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => handlePagination(1)}
                >
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => handlePagination(pageValue - 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                {getPageNumbers().map((n, i) =>
                  n === "..." ? (
                    <span key={"e" + i} className="px-1">
                      ...
                    </span>
                  ) : (
                    <button
                      type="button"
                      key={n}
                      style={pageBtn(n === pageValue, false)}
                      onClick={() => handlePagination(n)}
                    >
                      {n}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => handlePagination(pageValue + 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => handlePagination(totalPage)}
                >
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <Modal size="lg" centered show={show} onHide={() => setShow(false)}>
          <Modal.Header
            closeButton
            closeVariant="white"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderBottom: "none",
            }}
          >
            <Modal.Title
              className="d-flex align-items-center gap-2"
              style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}
            >
              <i className="bi bi-eye-fill"></i> Supplier Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 24, background: "#f8f9fe" }}>
            {viewLoading || !viewSupplier ? (
              <ContentLoader loaderType="circleSpin" />
            ) : (
              <DetailView
                fields={[
                  { label: "Supplier Code", value: viewSupplier.supplier_code },
                  { label: "Agency Name", value: viewSupplier.agency_name },
                  { label: "Head Person", value: viewSupplier.head_person },
                  { label: "Mobile", value: viewSupplier.mobile },
                  { label: "Email", value: viewSupplier.email },
                  { label: "Address", value: viewSupplier.address },
                  { label: "Pincode", value: viewSupplier.pincode },
                  { label: "City", value: viewSupplier.city?.name },
                  { label: "State", value: viewSupplier.state?.name },
                  { label: "Country", value: viewSupplier.country?.name },
                  {
                    label: "Wallet Balance",
                    value: viewSupplier.wallet?.current_balance,
                  },
                  {
                    label: "Hold Balance",
                    value: viewSupplier.wallet?.hold_balance,
                  },
                  { label: "Status", value: viewSupplier.status, badge: true },
                ]}
              />
            )}
          </Modal.Body>
        </Modal>
      </section>
    </>
  );
};

export default SupplierList;
